import { useMemo } from 'react';
import { useCustom } from '@refinedev/core';
import { Bot, Loader2, CheckCircle2, Ban } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { DataTable, type Column } from '@/components/tables';
import { getUserBotLinkStatusLabel } from '@/lib/translations';

interface UserBotLinkRow {
  botId: number;
  botName: string;
  botUsername: string | null;
  status: 'ACTIVE' | 'BLOCKED';
  firstSeenAt: string;
  lastSeenAt: string;
  blockedAt: string | null;
  unblockedAt: string | null;
}

interface UserBotsProps {
  userId: number;
}

const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) {
    return '—';
  }
  return new Date(dateString).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const getStatusBadge = (status: string) => {
  const label = getUserBotLinkStatusLabel(status);

  if (status === 'BLOCKED') {
    return (
      <Badge variant="destructive">
        <Ban className="h-3 w-3 mr-1" />
        {label}
      </Badge>
    );
  }

  if (status === 'ACTIVE') {
    return (
      <Badge className="bg-green-500/10 text-green-500 hover:bg-green-500/20">
        <CheckCircle2 className="h-3 w-3 mr-1" />
        {label}
      </Badge>
    );
  }

  return <Badge variant="outline">{label}</Badge>;
};

export function UserBots({ userId }: UserBotsProps) {
  const botsQuery = useCustom<UserBotLinkRow[]>({
    url: `/users/${userId}/bots`,
    method: 'get',
  });

  const isLoading = botsQuery.query?.isLoading ?? false;
  const bots: UserBotLinkRow[] = useMemo(
    () => botsQuery.query?.data?.data ?? [],
    [botsQuery.query?.data?.data],
  );

  const columns: Column<UserBotLinkRow & { id: number }>[] = [
    {
      key: 'botName',
      header: 'Бот',
      render: (_, record) => (
        <div className="flex items-center gap-2">
          <Bot className="h-4 w-4 text-muted-foreground" />
          <div className="flex flex-col">
            <span className="font-medium text-sm">{record.botName}</span>
            {record.botUsername && (
              <a
                href={`https://t.me/${record.botUsername}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-500 hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                @{record.botUsername}
              </a>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Статус',
      render: (_, record) => getStatusBadge(record.status),
    },
    {
      key: 'firstSeenAt',
      header: 'Первое обращение',
      render: (value) => (
        <span className="text-sm text-muted-foreground">{formatDate(value as string)}</span>
      ),
    },
    {
      key: 'lastSeenAt',
      header: 'Последнее обращение',
      render: (value) => (
        <span className="text-sm text-muted-foreground">{formatDate(value as string)}</span>
      ),
    },
    {
      key: 'blockedAt',
      header: 'Заблокировал',
      render: (_, record) => (
        <span className="text-sm text-muted-foreground">{formatDate(record.blockedAt)}</span>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (bots.length === 0) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        <Bot className="mx-auto mb-2 h-12 w-12 opacity-50" />
        <p>Пользователь не взаимодействовал ни с одним ботом</p>
      </div>
    );
  }

  // У UserBotLinkRow нет собственного id — DataTable требует поле id (rowKey),
  // используем botId как стабильный ключ строки.
  const data = bots.map((bot) => ({ ...bot, id: bot.botId }));

  return (
    <DataTable
      columns={columns}
      data={data}
      total={data.length}
      page={1}
      pageSize={100}
      onPageChange={() => {}}
      emptyMessage="Нет данных по ботам"
      rowKey="id"
    />
  );
}
